/* oxlint-disable react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    Body1,
    Flex,
    Heading1,
    PinInput,
    Stack,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import { useHotkeys } from '@myphx-engineering/phx-design-system/hooks';

import { checkAccountLockoutApi } from '../../api/check-account-lockout.api';
import { verifyTotpApi } from '../../api/totp.api';
import { PageLayout } from '../../components/layout/PageLayout';
import { CONSTANTS } from '../../constants';
import { telemetryInstance } from '../../instrumentation';
import { routes } from '../../routes';
import { getErrorMessage } from '../../utils/get-error-message';

export type TotpVerifyPageState = {
    phoneNumber: string;
    password: string;
    callbackUrl: string;
    kratosIdentityId: string;
    kratosSessionToken?: string;
};

const TotpVerifyPage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { setBackOverrideState } = useNavBar();

    const state = location?.state as TotpVerifyPageState;

    // Validate state
    if (!state || !state.phoneNumber || !state.password) {
        navigate({
            pathname: routes.signIn,
            search: window.location.search,
        });
        return null;
    }

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [code, setCode] = useState<string>('');

    const isFormValid = code.length === 6;

    const onNavigateBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    useEffect(() => {
        setBackOverrideState({
            overrideFn: onNavigateBack,
        });

        return () => setBackOverrideState({ overrideFn: null });
    }, [onNavigateBack, setBackOverrideState]);

    const onVerifyTOTPClick = async (verificationCode: string) => {
        telemetryInstance.logEvent({
            name: 'TOTPVerifySubmitted',
            source: location.pathname,
        });
        setErrorMessage(undefined);
        setLoading(true);

        try {
            // Verify TOTP code via backend
            const { success, error, sessionId } = await verifyTotpApi(
                state.kratosSessionToken || '',
                verificationCode,
                state.phoneNumber
            );

            if (success) {
                telemetryInstance.logEvent({
                    name: 'TOTPVerifySuccess',
                    source: location.pathname,
                });

                // TOTP verified! Session is now upgraded to aal2
                // Redirect with the sessionId (not the token) in the URL
                const redirectSessionId = sessionId || state.kratosIdentityId;
                const redirectUrl = `${state.callbackUrl}&session=${redirectSessionId}&authMethod=${CONSTANTS.defaultAuth}`;

                window.location.replace(redirectUrl);
            } else {
                // If user is locked out (429), redirect back to sign-in with lockout flag and phone number
                if (error === '429_MaxAttemptReached') {
                    navigate(
                        {
                            pathname: routes.signIn,
                            search: window.location.search,
                        },
                        {
                            state: {
                                lockoutError: true,
                                phoneNumber: state.phoneNumber,
                            },
                        }
                    );
                    return;
                }

                const errorType = error || '400_InvalidCode';

                // If invalid code, check remaining attempts
                if (errorType === '400_InvalidCode') {
                    const lockoutStatus = await checkAccountLockoutApi(
                        state.phoneNumber
                    );
                    setLoading(false);
                    setErrorMessage(
                        getErrorMessage(
                            t,
                            errorType,
                            lockoutStatus.remainingAttempts
                        )
                    );
                    setCode('');
                    telemetryInstance.logError({
                        source: location.pathname,
                        exception: new Error(
                            error || 'TOTP verification failed'
                        ),
                    });
                    return;
                }

                setLoading(false);
                setErrorMessage(getErrorMessage(t, errorType));
                setCode('');
                telemetryInstance.logError({
                    source: location.pathname,
                    exception: new Error(error || 'TOTP verification failed'),
                });
            }
        } catch (error) {
            setLoading(false);
            setErrorMessage(getErrorMessage(t, '400_InvalidCode'));
            setCode('');
            telemetryInstance.logError({
                source: location.pathname,
                exception:
                    error instanceof Error ? error : new Error(String(error)),
            });
        }
    };

    useHotkeys([
        [
            'Enter',
            () => {
                if (isFormValid) {
                    onVerifyTOTPClick(code);
                }
            },
        ],
    ]);

    return (
        <PageLayout
            loadingOverlay={loading}
            heading={
                <Stack gap="sm">
                    <Heading1>{t('totpVerifyPage.title')}</Heading1>
                    <Body1>{t('totpVerifyPage.description')}</Body1>
                </Stack>
            }
        >
            <Stack gap="md">
                <PinInput
                    length={6}
                    size="xl"
                    gap="md"
                    // oxlint-disable-next-line jsx-a11y/no-autofocus -- tracked in docs/oxlint-violations.md
                    autoFocus
                    oneTimeCode
                    placeholder=""
                    value={code}
                    onChange={setCode}
                    onComplete={onVerifyTOTPClick}
                    error={errorMessage}
                />
            </Stack>
            <Flex justify="left">
                <Body1>{t('totpVerifyPage.codeRefresh')}</Body1>
            </Flex>
        </PageLayout>
    );
};

export default TotpVerifyPage;
